import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Clock, Package, Download, Upload, AlertTriangle, Settings as SettingsIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n/translations';
import { toast } from '../hooks/use-toast';

const DEMO_SCENARIO = [
  { delay: 0, type: 'Deposit', locker: 1, size: 'S', user: 'Morning User A', pin: '1234' },
  { delay: 2000, type: 'Deposit', locker: 5, size: 'M', user: 'Morning User B', pin: '5678' },
  { delay: 3000, type: 'Deposit', locker: 9, size: 'L', user: 'Courier Service', pin: '9012' },
  { delay: 4000, type: 'Retrieve', locker: 3, method: 'PIN', user: 'Anna K.' },
  { delay: 5000, type: 'Deposit', locker: 4, size: 'S', user: 'Lunch User', pin: '3456' },
  { delay: 6000, type: 'Retrieve', locker: 5, method: 'RFID', user: 'Badge A' },
  { delay: 7000, type: 'Tamper', locker: 7, alert: 'Unauthorized access attempt' },
  { delay: 8000, type: 'Deposit', locker: 8, size: 'M', user: 'Afternoon User', pin: '7890' },
  { delay: 9000, type: 'Battery-low', locker: 11, alert: 'Battery at 15%' },
  { delay: 10000, type: 'Retrieve', locker: 9, method: 'PIN', user: 'Courier Service' },
  { delay: 11000, type: 'Deposit', locker: 10, size: 'L', user: 'Evening User', pin: '2468' },
  { delay: 12000, type: 'Retrieve', locker: 1, method: 'RFID', user: 'Badge A' },
  { delay: 13000, type: 'Admin', locker: 7, action: 'Reset alert', user: 'Admin' },
  { delay: 14000, type: 'Deposit', locker: 2, size: 'S', user: 'Late User', pin: '1357' },
  { delay: 15000, type: 'Retrieve', locker: 4, method: 'PIN', user: 'Lunch User' },
  { delay: 16000, type: 'Retrieve', locker: 8, method: 'RFID', user: 'Badge A' },
];

const Timeline = () => {
  const { logs, whiteLabel, depositParcel, retrieveParcel, simulateEvent, language } = useAuth();
  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';
  const t = (key, replacements) => getTranslation(key, language, replacements);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const timeoutRef = useRef(null);

  const getEventIcon = (type) => {
    switch (type) {
      case 'Deposit':
        return <Upload size={20} className="text-blue-600" />;
      case 'Retrieve':
        return <Download size={20} className="text-green-600" />;
      case 'Tamper':
      case 'Battery-low':
        return <AlertTriangle size={20} className="text-yellow-600" />;
      case 'Admin':
        return <SettingsIcon size={20} className="text-purple-600" />;
      default:
        return <Package size={20} className="text-gray-600" />;
    }
  };

  const playScenario = () => {
    if (currentEventIndex >= DEMO_SCENARIO.length) {
      setIsPlaying(false);
      toast({
        title: t('scenarioComplete'),
        description: t('allEventsReplayed'),
      });
      return;
    }

    const event = DEMO_SCENARIO[currentEventIndex];
    const delay = currentEventIndex === 0 ? 0 : 2000 / speed;

    timeoutRef.current = setTimeout(() => {
      // Execute the event
      if (event.type === 'Deposit') {
        depositParcel(event.locker, event.size, event.pin, event.user);
      } else if (event.type === 'Retrieve') {
        retrieveParcel(event.locker, event.method, event.user);
      } else if (event.type === 'Tamper' || event.type === 'Battery-low') {
        simulateEvent(event.type, event.locker, { alert: event.alert });
      } else if (event.type === 'Admin') {
        simulateEvent(event.type, event.locker, { action: event.action, user: event.user });
      }

      setCurrentEventIndex(prev => prev + 1);
    }, delay);
  };

  useEffect(() => {
    if (isPlaying) {
      playScenario();
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isPlaying, currentEventIndex, speed]);

  const handlePlayPause = () => {
    if (currentEventIndex >= DEMO_SCENARIO.length) {
      setCurrentEventIndex(0);
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentEventIndex(0);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const toggleSpeed = () => {
    setSpeed(prev => prev === 1 ? 2 : 1);
  };

  // Sort logs by timestamp descending
  const sortedLogs = [...logs].sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold text-[#1A1A1A]">{t('timeline')}</h1>
        <p className="text-gray-600">{t('viewActivityHistory')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('scenarioPlayback')}</CardTitle>
          <CardDescription>
            {t('replayFullDay')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
              <div className="flex items-center space-x-2">
                <Clock size={20} style={{ color: primaryColor }} />
                <span className="text-sm font-medium">
                  {t('eventCount', { current: currentEventIndex, total: DEMO_SCENARIO.length })}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                {t('speedValue', { speed })}
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${(currentEventIndex / DEMO_SCENARIO.length) * 100}%`,
                  backgroundColor: primaryColor
                }}
              />
            </div>

            <div className="flex space-x-2">
              <Button
                onClick={handlePlayPause}
                className="flex-1"
                style={{ backgroundColor: primaryColor }}
              >
                {isPlaying ? <Pause className="mr-2" size={16} /> : <Play className="mr-2" size={16} />}
                {isPlaying ? t('pause') : currentEventIndex >= DEMO_SCENARIO.length ? t('replay') : t('playScenario')}
              </Button>
              
              <Button
                onClick={toggleSpeed}
                variant="outline"
              >
                {speed}×
              </Button>
              
              <Button
                onClick={handleReset}
                variant="outline"
              >
                <RotateCcw size={16} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('eventHistory')}</CardTitle>
          <CardDescription>{t('chronologicalOrder')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {sortedLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('noEventsYet')}</p>
            ) : (
              sortedLogs.map((log, index) => (
                <div 
                  key={log.id}
                  className="flex items-center space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-shrink-0">
                    {getEventIcon(log.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-[#1A1A1A]">{log.type}</span>
                      <span className="text-sm text-gray-600">{t('locker')} #{log.locker}</span>
                      {log.size && <span className="text-xs px-2 py-1 rounded bg-white text-gray-700">{log.size}</span>}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {log.user && <span>{t('user')}: {log.user} • </span>}
                      {log.pin && <span>{t('pin')}: {log.pin} • </span>}
                      {log.method && <span>{t('method')}: {log.method} • </span>}
                      {log.alert && <span>{t('alert')}: {log.alert} • </span>}
                      {log.action && <span>{t('action')}: {log.action} • </span>}
                      <span className="text-xs">{log.timestamp.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Timeline;
